import React from "react";
import {
  OrganizationSize,
  ProbabilityLevel,
  AttackCategoryData,
  ImpactLevel,
  CyFunLevel,
  SectorAnalysisDiff,
} from "@/types/risk-analysis";
import { ACTORI_AMENINTARE } from "@/data/risk-sectors";
import { ColoredDropdown } from "@/components/common/ColoredDropdown";
import { IMPACT_OPTIONS, PROBABILITY_OPTIONS } from "@/constants/risk-options";
import { RiskScoreCell } from "./RiskScoreCell";
import { cn, formatScore } from "@/lib/utils";
import { useSidebar } from "../ui/sidebar";
import { getCyFunLevelData } from "../../utils/risk-calculator";

interface RiskMatrixTableProps {
  attackMatrix: AttackCategoryData[];
  actorTotals: Record<string, number>;
  totalScore: number;
  orgSize: OrganizationSize;
  sectorName: string;
  onProbabilityChange: (
    categoryId: string,
    actorId: string,
    probability: ProbabilityLevel
  ) => void;
  onImpactChange?: (categoryId: string, impact: ImpactLevel) => void;
  disabled?: boolean;
  cyfunLevel: CyFunLevel;
  diff?: SectorAnalysisDiff | null;
}

export function RiskMatrixTable({
  attackMatrix,
  actorTotals,
  totalScore,
  orgSize,
  sectorName,
  onProbabilityChange,
  onImpactChange,
  disabled = false,
  cyfunLevel,
  diff,
}: RiskMatrixTableProps) {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  // Group actors by competence level
  const commonActors = ACTORI_AMENINTARE.filter(
    (a) => a.categorie === "Common skills"
  );
  const extendedActors = ACTORI_AMENINTARE.filter(
    (a) => a.categorie === "Extended Skills"
  );

  const orgSizeValue = orgSize === "S" ? "1" : orgSize === "M" ? "2" : "3";

  const maxWidth = isCollapsed
    ? "max-w-[calc(100vw-148px)]"
    : "max-w-[calc(100vw-356px)]";

  return (
    <>
      <div className={cn("w-full overflow-x-auto mb-14 flex", maxWidth)}>
        <table className="w-full border-collapse border border-black border-2">
          {/* Header - Three rows */}
          <thead className="sticky top-0 z-10">
            {/* Row 1: Sector name and competence categories */}
            <tr className="bg-white">
              <th
                colSpan={2}
                className="border border-black border-1 px-2 py-2 text-left font-bold text-lg bg-blue-100"
              >
                {sectorName.toUpperCase()}
              </th>
              <th
                rowSpan={2}
                className="border-t border-l border-b-2 border-r border-black [border-right-style:dashed] px-2 py-1 text-center text-xs font-bold bg-white italic"
              >
                Categorii de actori ai amenințării
              </th>
              {ACTORI_AMENINTARE.map((actor, idx) => (
                <th
                  key={actor.id}
                  colSpan={2}
                  className="border border-black [border-left-style:dashed] [border-bottom-style:dashed] px-2 py-1 text-center text-xs font-semibold italic"
                >
                  {actor.categorie === "Common skills"
                    ? "Competențe comune"
                    : "Competențe extinse"}
                </th>
              ))}
            </tr>

            {/* Row 2: Dimensiunea entității and actor names */}
            <tr className="bg-white">
              <th className="border border-black border-2 px-2 py-1 text-left text-xs font-bold bg-blue-300">
                Dimensiunea entității <br />
                (Mare/Mijlocie/Mică,Micro = 3/2/1)
              </th>
              <th className="border border-black border-2 px-2 py-1 text-center text-sm font-bold bg-blue-300">
                {orgSizeValue}
              </th>
              {/* C2 continues from C1 rowSpan */}
              {ACTORI_AMENINTARE.map((actor, idx) => (
                <th
                  key={actor.id}
                  colSpan={2}
                  className="border border-black [border-left-style:dashed] [border-top-style:dashed] border-b-2 px-2 py-1 text-center text-xs font-bold"
                >
                  {actor.nume}
                </th>
              ))}
            </tr>

            {/* Row 3: Column headers */}
            <tr className="bg-slate-50 dark:bg-slate-600">
              <th className="border border-black border-2 px-2 py-1 text-left text-sm font-bold bg-blue-700 text-white min-w-[320px]">
                Categoria atacului cibernetic
              </th>
              <th className="border border-black border-2 px-2 text-center text-sm font-bold bg-blue-700 text-white min-w-[85px] italic">
                Natura
                <br />
                atacului
              </th>
              <th className="border border-black border-2 px-2 py-1 text-center text-xs font-bold bg-blue-300">
                Impact
              </th>
              {ACTORI_AMENINTARE.map((actor, idx) => (
                <React.Fragment key={actor.id}>
                  <th className="border border-black border-2 px-1 py-1 text-xs text-center font-bold bg-blue-300">
                    Probabilitate
                  </th>
                  <th
                    className={cn(
                      "border border-black border-2 px-1 py-1 text-xs text-center font-bold bg-blue-300",
                      idx === commonActors.length - 1 && "border-r-2"
                    )}
                  >
                    Valoarea
                    <br />
                    riscului
                  </th>
                </React.Fragment>
              ))}
            </tr>
          </thead>

          {/* Body */}
          <tbody>
            {attackMatrix.map((category, catIdx) => {
              // Find changes for this category
              const categoryChange = diff?.categoryChanges.find(
                (c) => c.categoryId === category.categoryId
              );
              const hasImpactChange = !!categoryChange?.impactChange;

              return (
                <tr key={category.categoryId} className="bg-white">
                  {/* Column A: Category name */}
                  <td className="border border-black border-dashed px-2 text-sm bg-blue-300 font-bold">
                    {category.categoryName}
                  </td>

                  {/* Column B: Natura atacului (1 for global, 2 for targeted) */}
                  <td className="border border-black border-dashed px-2 text-center text-sm font-bold">
                    {category.tipAtac === "global" ? "1" : "2"}
                  </td>

                  {/* Column C: Impact (shared across all actors) */}
                  <td className="border border-dashed border-black p-0">
                    {onImpactChange ? (
                      <ColoredDropdown
                        value={category.impact}
                        onChange={(impact) =>
                          onImpactChange(category.categoryId, impact)
                        }
                        options={IMPACT_OPTIONS}
                        disabled={disabled}
                        hasChanged={hasImpactChange}
                      />
                    ) : (
                      <div className="text-center text-sm">
                        {category.impact}
                      </div>
                    )}
                  </td>

                  {/* Columns D-M: Actor pairs (Probability, Risk Score) */}
                  {ACTORI_AMENINTARE.map((actor, idx) => {
                    const actorData = category.actors[actor.id];

                    // Check if this actor's probability changed
                    const hasProbabilityChange =
                      !!categoryChange?.probabilityChanges.find(
                        (pc) => pc.actorId === actor.id
                      );

                    // Score changes when either probability OR impact changes
                    const hasScoreChange =
                      hasImpactChange || hasProbabilityChange;

                    return (
                      <React.Fragment key={actor.id}>
                        {/* Probability dropdown */}
                        <td className="border border-black border-dashed">
                          <ColoredDropdown
                            value={actorData.probability}
                            onChange={(prob) =>
                              onProbabilityChange(
                                category.categoryId,
                                actor.id,
                                prob
                              )
                            }
                            options={PROBABILITY_OPTIONS}
                            disabled={disabled}
                            className="min-w-[100px] text-xs"
                            hasChanged={hasProbabilityChange}
                          />
                        </td>

                        {/* Risk score */}
                        <td
                          className={cn(
                            "border border-black border-dashed text-center font-medium",
                            hasScoreChange && "font-bold"
                          )}
                        >
                          {formatScore(actorData.score)}
                          {hasScoreChange && "*"}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>

          {/* Footer with totals */}
          <tfoot className="bg-white sticky bottom-0 z-10 font-bold">
            <tr>
              <td
                colSpan={4}
                className="border border-black px-2 py-2 text-left text-lg font-bold"
              >
                TOTAL
              </td>
              {ACTORI_AMENINTARE.map((actor, idx) => (
                <React.Fragment key={actor.id}>
                  {idx === 0 ? (
                    // First actor: only Total (Probabilitate column consumed by TOTAL span)
                    <td className="border border-black px-2 text-center">
                      {formatScore(actorTotals[actor.id] || 0)}
                    </td>
                  ) : (
                    // Actors 2-5: Empty + Total
                    <>
                      {/* Empty cell under Probabilitate */}
                      <td className="border border-black"></td>
                      {/* Total score for this actor */}
                      <td
                        className={cn("border border-black px-2 text-center")}
                      >
                        {formatScore(actorTotals[actor.id] || 0)}
                      </td>
                    </>
                  )}
                </React.Fragment>
              ))}
            </tr>
          </tfoot>
        </table>
        <table className="border-collapse border-2 border-l-0 border-black self-end">
          <thead>
            <tr>
              <th className="border border-black border-l-1 px-4 bg-blue-200 text-center text-sm font-bold">
                Scor
              </th>
              <th className="border border-black px-4 bg-blue-200 text-center text-sm font-bold min-w-[159px]">
                Categorie de
                <br />
                cerințe
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-black px-4 py-2 text-center font-bold text-lg">
                {formatScore(totalScore)}
              </td>
              <td
                className={cn(
                  "border border-black px-4 text-center font-bold text-base",
                  getCyFunLevelData(cyfunLevel).color
                )}
              >
                {getCyFunLevelData(cyfunLevel).label}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}
